import { Store } from "Application/Store/Store";
import { SET_INTERFERENCE, SET_STRIPING } from "Application/Store/Actions/CollisionTypes";
import {MODE, SET_SELECTED_COMPARATIVE_VERSION, SET_SELECTED_VERSION} from "Application/Store/Actions/ViewportTypes";
import { destroyTeethOutline } from "Domain/Mesh/Handler/Composer";
import {
    applyModification,
    disableSelectionMode,
    enableSelectionMode,
    hasAnyToothBeenModified,
    resetAllTeethToInitialPosition,
    saveModifications,
    removeModification,
    refreshModifications,
    clearSessionMovementHistory
} from "Domain/Mesh/Handler/MeshSelectorEvent";
import { unDisplayTeethIntersection } from "Domain/Scene/Handler/CollisionHandler";
import { threeEngine } from 'Domain/Scene/Engine/ThreeEngine';
import { MainCameraPosition } from "../Handler/CameraHandler";
import { notifyFileModified } from "Ui/Layer/Notification";
import { toogleAnimationVisible } from './SceneManager';
import { savingPopup, savedPopup, deletingPopup } from "Ui/Layer/popup";
import { resetViewportCamera } from 'Domain/Scene/Provider/ViewportCameraProvider';

/**
 * Gestionnaire centralisé de l'état d'édition
 */
export const EditorManager = {
    /**
     * Active le mode édition
     * @param {Function} setOnEditMode - Fonction de mise à jour de l'état React
     */
    enterEditMode: (setOnEditMode) => {
        enableSelectionMode();
        setOnEditMode(true);
        Store.dispatch({ type: MODE, payload: 'edition' });

        // Cacher certains éléments en mode édition
        Store.dispatch({ type: 'STRIPPING_VISIBLE', payload: false });
        Store.dispatch({ type: 'FOOTER_VISIBLE', payload: false });
        Store.dispatch(toogleAnimationVisible('none'));

        // Désactiver l'affichage des collisions
        setTimeout(() => unDisplayTeethIntersection(), 200);

        // Changer le mode de contrôle de la caméra
        MainCameraPosition(0, -180, 0);
        threeEngine.changeControl();
    },

    /**
     * Quitte le mode édition
     * @param {Function} setOnEditMode - Fonction de mise à jour de l'état React
     * @param {Boolean} saveChanges - Si true, sauvegarde les modifications, sinon annule
     */
    exitEditMode: async (setOnEditMode, saveChanges = false) => {
        const versionBeforeExit = Store.getState().ViewportReducer.selectedVersion;

        if (saveChanges) {
            savingPopup();

            await saveModifications();
            await refreshModifications();

            // Après la sauvegarde, récupérer l'ID de la nouvelle version
            const versions = Store.getState().ViewportReducer.versions;
            const newVersionId = versions[versions.length - 1].value;

            Store.dispatch({
                type: SET_SELECTED_VERSION,
                payload: newVersionId
            });

            clearSessionMovementHistory();
            notifyFileModified(newVersionId);
        } else {
            resetAllTeethToInitialPosition();
            clearSessionMovementHistory();
            if (parseInt(versionBeforeExit, 10) !== -1) {
                await applyModification(versionBeforeExit);
            }
        }

        // Réinitialiser l'interface
        disableSelectionMode();
        destroyTeethOutline();
        setOnEditMode(false);

        // Mettre à jour l'état Redux
        Store.dispatch({ type: MODE, payload: 'visualization' });
        Store.dispatch({ type: SET_INTERFERENCE, payload: false });
        Store.dispatch({ type: SET_STRIPING, payload: false });

        // Restaurer l'affichage des collisions
        setTimeout(() => unDisplayTeethIntersection(true), 200);

        // For original version only: force back to maloc/setup with player visible
        if (!saveChanges && parseInt(versionBeforeExit, 10) === -1) {
            await applyModification(-1);
            Store.dispatch(toogleAnimationVisible('flex'));
        }

        // Changer le mode de contrôle de la caméra
        MainCameraPosition(0, -180, 0);
        const mode = Store.getState().ViewportReducer.viewportMode;
        if (mode) {
            resetViewportCamera(mode);
        }
        threeEngine.changeControl();

        if (saveChanges) {
            await new Promise(resolve => requestAnimationFrame(() => resolve()));
            savedPopup();
        }
    },

    /**
     * Vérifie si des modifications ont été apportées
     * @returns {Boolean} true si des modifications ont été faites
     */
    hasChanges: () => {
        return hasAnyToothBeenModified();
    },

    /**
     * Supprime la version courante
     */
    deleteCurrentVersion: async () => {
        deletingPopup();
        await new Promise(resolve => requestAnimationFrame(() => resolve()));
        await removeModification();
        unDisplayTeethIntersection(true);
        savedPopup();
    },

    /**
     * Sélectionne une version spécifique
     * @param {Number} versionId - Identifiant de la version
     */
    selectVersion: async (versionId) => {
        const numericVersionId = parseInt(versionId, 10);
        Store.dispatch({
            type: SET_SELECTED_VERSION,
            payload: numericVersionId
        });
        clearSessionMovementHistory();
        await applyModification(numericVersionId);
    },

    selectComparativeVersion: async (versionId) => {
        const numericVersionId = parseInt(versionId, 10);
        Store.dispatch({
            type: SET_SELECTED_COMPARATIVE_VERSION,
            payload: numericVersionId
        });
        clearSessionMovementHistory();
        await applyModification(numericVersionId, true);
    }
};
